import { CheckOutlined, GlobalOutlined } from '@ant-design/icons';
import { Button, Dropdown, type ButtonProps, type MenuProps } from '@yimoka/antd';
import { useSyncExternalStore } from 'react';

import { getEnv } from '@/env';
import { useTranslation } from '@/locales/i18n';
import {
  getCurrentLanguage,
  setCurrentLanguage,
  subscribeLanguage,
  SUPPORT_LANGUAGES,
} from '@/locales/language';

export interface LanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
  size?: ButtonProps['size'];
}

/** 基座与 federation remote 共用的语言入口，并按运行时配置统一控制所有挂载位置。 */
export function LanguageSwitcher({ className, showLabel = true, size }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const language = useSyncExternalStore(subscribeLanguage, getCurrentLanguage, getCurrentLanguage);

  // 只在公共入口收口展示策略，避免 SSO、管理台和 Workspace 各自维护条件而产生差异。
  if (getEnv().showLanguageSwitcher === false) return null;

  const current = SUPPORT_LANGUAGES.find(item => item.value === language) ?? SUPPORT_LANGUAGES[0];
  const items: MenuProps['items'] = SUPPORT_LANGUAGES.map(item => ({
    icon: item.value === language ? <CheckOutlined /> : <span style={{ display: 'inline-block', width: 14 }} />,
    key: item.value,
    label: item.label,
  }));

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        selectedKeys: [language],
        onClick: ({ key }) => setCurrentLanguage(key),
      }}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        aria-label={t('Common:switchLanguage')}
        className={className}
        data-testid="language-switcher"
        icon={<GlobalOutlined />}
        size={size}
        type="text"
      >
        {showLabel ? current.label : null}
      </Button>
    </Dropdown>
  );
}

export default LanguageSwitcher;
